/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { CartApi } from '@api';
import { Article, ContactNode, Enrolment, FieldHeading, Membership, Voucher, WaitingList } from '@api';
import { Token } from 'src/js/model/index';
import { DefaultHttpService } from '../common/services/HttpService';
import { StoreCartContact, StoreCartItem, StoreCartRequest } from '../model/checkout/request/StoreCart';

class CartService {
  private api = new CartApi(new DefaultHttpService());

  public delete(id: string): Promise<any> {
    return this.api.remove(Number(id))
  }

  public create(id: string, checkout: StoreCartRequest): Promise<Token | string> {
    const validationError = this.validateCart(checkout);
    if (validationError) {
      if (validationError === 'Products are missing' && checkout.payerId) {
        return this.api.remove(Number(checkout.payerId));
      }
      console.error(validationError);
      return Promise.resolve(validationError);
    }
    return this.api.create({data:JSON.stringify(checkout)}, Number(id));
  }

  public validateCartItem(item: StoreCartItem, error: string): boolean {
    switch (true) {
      case Number.isNaN(Number(item.id)):
        error = 'ProductId is missing';
        return true;
      case (typeof item.selected !== 'boolean'):
        error = '"selected" field has wrong format';
        return true;
      case (typeof item.quantity !== 'number'):
        error = '"quantity" field has wrong format';
        return true;
      case (item.customFields?.length && item.customFields?.some(c => c.fields.some(cf => Number.isNaN(Number(cf.id))))):
        error = 'Custom fields has wrong format';
        return true;
      default:
        return true;
    }
  }

  public validateCart({
    payerId,
    checkoutURL,
    contacts
  }: StoreCartRequest): string {
    let contactsError: string = null;

    switch (true) {
      case !payerId:
        return 'PayerId is missing';
      case !checkoutURL:
        return 'CheckoutURL is missing';
      case !contacts:
        return 'ContactsNodes are missing';
      case contacts.some(c => {
        switch (true) {
          case Number.isNaN(Number(c.contactId)):
            contactsError = 'ContactId is missing';
            return true;
          case (!c.classes && !c.products && !c.applications && !c.waitingCourses):
            contactsError = 'Products are missing';
            return true;
          case c.classes?.some(ci => this.validateCartItem(ci, contactsError)):
          case c.applications?.some(ci => this.validateCartItem(ci, contactsError)):
          case c.waitingCourses?.some(ci => this.validateCartItem(ci, contactsError)):
          case c.products?.some(ci => this.validateCartItem(ci, contactsError)):
            return true;
        }
        return false;
      }):
        return contactsError;
    }
    return null;
  }

  public get(id: number): Promise<any> {
    return this.api.get(id);
  }

  public mapCustomFields = (fh: FieldHeading[]) => (fh.length
    && fh.some((f) => f.fields.some((fhf) => fhf.value))
    ? {
      customFields: fh.map((f) => ({
        fields: f.fields
          .filter((fhf) => fhf.value)
          .map(({ id, value }) => ({ id, value }))
      }))
    }
    : {});

  public mapCourse = (course: Enrolment): StoreCartItem => ({
    id: course.classId,
    selected: course.selected,
    ...this.mapCustomFields(course.fieldHeadings)
  });

  public mapWaitingCourse = (course: WaitingList): StoreCartItem => ({
    id: course.courseId,
    selected: course.selected,
    ...this.mapCustomFields(course.fieldHeadings)
  });

  public mapProduct = (product: Article | Membership | Voucher): StoreCartItem => ({
    id: product.productId,
    selected: product.selected,
    quantity: product instanceof Membership ? 1 : product.quantity,
    ...this.mapCustomFields(product.fieldHeadings)
  });

  public contactNodeToCartContact = (node: ContactNode): StoreCartContact => {
    const cartContact: StoreCartContact = {
      contactId: node.contactId,
      applications: node.applications.filter(i => i).map(this.mapCourse),
      classes: node.enrolments.filter(i => i).map(this.mapCourse),
      waitingCourses: node.waitingLists.filter(i => i).map(this.mapWaitingCourse),
      products: [
        ...node.articles.filter(i => i).map(this.mapProduct),
        ...node.vouchers.filter(i => i).map(this.mapProduct),
        ...node.memberships.filter(i => i).map(this.mapProduct),
      ],
    };

    if (!cartContact.applications.length) {
      delete cartContact.applications;
    }
    if (!cartContact.classes.length) {
      delete cartContact.classes;
    }
    if (!cartContact.waitingCourses.length) {
      delete cartContact.waitingCourses;
    }
    if (!cartContact.products.length) {
      delete cartContact.products;
    }

    return cartContact;
  };
}

export default new CartService();